// Canonical order status values for the VAISP processing queue.
//
// Two INDEPENDENT axes live here and must not be merged:
//
//   • ORDER_PROCESSING_STATUS — Sliceal's own funnel (admin-service
//     ORDER_PROCESSING_STATUS). What the KPI cards filter on.
//   • VASP_ORDER_STATUS — what Ctrl-Alt reports (admin-service VASP_ORDER_STATUS).
//
// They collide on seven keys with *different meanings*: on the local axis
// `pending` means "approved, queued, VASP not started"; on the vendor axis it
// means "Ctrl-Alt accepted the order". `rejected` is "admin rejected" vs
// "Ctrl-Alt refused". One label map cannot serve both, which is why the vendor
// axis gets its own colour/label maps below. Same split as
// `@shared/constants/vaspStatus.ts` for the user axis.

type ChipColor = 'primary' | 'neutral' | 'dark' | 'success' | 'warning' | 'danger'

export const ORDER_PROCESSING_STATUS = {
  FAILED: 'failed',
  PENDING: 'pending',
  AWAITING_ISSUANCE: 'awaiting_issuance',
  DISTRIBUTING_TOKENS: 'distributing_tokens',
  TOKENS_DISTRIBUTED: 'tokens_distributed',
  AWAITING_COMPLETE: 'awaiting_complete',
  COMPLETED: 'completed',
} as const

export type OrderProcessingStatus =
  (typeof ORDER_PROCESSING_STATUS)[keyof typeof ORDER_PROCESSING_STATUS]

// The lanes the queue lists. `completed` is excluded — a completed order leaves
// the queue. `awaiting_complete` stays so legacy rows still surface under ALL even
// though real Ctrl-Alt no longer parks orders there.
export const VASP_QUEUE_PROCESSING_STATUSES: readonly OrderProcessingStatus[] = [
  ORDER_PROCESSING_STATUS.PENDING,
  ORDER_PROCESSING_STATUS.AWAITING_ISSUANCE,
  ORDER_PROCESSING_STATUS.DISTRIBUTING_TOKENS,
  ORDER_PROCESSING_STATUS.TOKENS_DISTRIBUTED,
  ORDER_PROCESSING_STATUS.AWAITING_COMPLETE,
  ORDER_PROCESSING_STATUS.FAILED,
]

// ── Vendor axis (Ctrl-Alt) ──────────────────────────────────────────────────

export const VASP_ORDER_STATUS = {
  AWAITING_OPT_IN: 'awaiting_opt_in',
  PROCESSING_OPT_IN: 'processing_opt_in',
  // The ledger is waiting for the investor's wallet to trust the token. Unlike
  // the two opt-in states above, this one is actionable from our side: the
  // broker holds the custody wallet, so an operator can open the trustline from
  // wallet management rather than waiting on the investor.
  AWAITING_EXTERNAL_OPT_IN: 'awaiting_external_opt_in',
  PENDING: 'pending',
  APPROVED: 'approved',
  AWAITING_ISSUANCE: 'awaiting_issuance',
  AWAITING_DISTRIBUTION: 'awaiting_distribution',
  DISTRIBUTING_TOKENS: 'distributing_tokens',
  TOKENS_DISTRIBUTED: 'tokens_distributed',
  AWAITING_REGISTRATION: 'awaiting_registration',
  AWAITING_APPROVAL: 'awaiting_approval',
  AWAITING_COMPLETE: 'awaiting_complete',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
  DELETED: 'deleted',
  CANCELLED: 'cancelled',
  TOKEN_DISTRIBUTION_REJECTED: 'token_distribution_rejected',
} as const

export type VaspOrderStatus = (typeof VASP_ORDER_STATUS)[keyof typeof VASP_ORDER_STATUS]

export const VASP_ORDER_TYPE = {
  CAPITAL_RAISE: 'capital_raise',
  SECONDARY: 'secondary',
} as const

export type VaspOrderType = (typeof VASP_ORDER_TYPE)[keyof typeof VASP_ORDER_TYPE]

const VASP_ORDER_STATUS_COLOR: Record<string, ChipColor> = {
  [VASP_ORDER_STATUS.AWAITING_OPT_IN]: 'neutral',
  [VASP_ORDER_STATUS.PROCESSING_OPT_IN]: 'primary',
  [VASP_ORDER_STATUS.AWAITING_EXTERNAL_OPT_IN]: 'warning',
  [VASP_ORDER_STATUS.PENDING]: 'warning',
  [VASP_ORDER_STATUS.APPROVED]: 'success',
  [VASP_ORDER_STATUS.AWAITING_ISSUANCE]: 'warning',
  [VASP_ORDER_STATUS.AWAITING_DISTRIBUTION]: 'warning',
  [VASP_ORDER_STATUS.DISTRIBUTING_TOKENS]: 'primary',
  [VASP_ORDER_STATUS.TOKENS_DISTRIBUTED]: 'success',
  [VASP_ORDER_STATUS.AWAITING_REGISTRATION]: 'warning',
  [VASP_ORDER_STATUS.AWAITING_APPROVAL]: 'warning',
  [VASP_ORDER_STATUS.AWAITING_COMPLETE]: 'warning',
  [VASP_ORDER_STATUS.COMPLETED]: 'success',
  [VASP_ORDER_STATUS.REJECTED]: 'danger',
  [VASP_ORDER_STATUS.DELETED]: 'neutral',
  [VASP_ORDER_STATUS.CANCELLED]: 'neutral',
  [VASP_ORDER_STATUS.TOKEN_DISTRIBUTION_REJECTED]: 'danger',
}

const VASP_ORDER_STATUS_LABEL: Record<string, string> = {
  [VASP_ORDER_STATUS.AWAITING_OPT_IN]: 'Awaiting investor opt-in',
  [VASP_ORDER_STATUS.PROCESSING_OPT_IN]: 'Processing opt-in',
  [VASP_ORDER_STATUS.AWAITING_EXTERNAL_OPT_IN]: 'Awaiting trustline',
  [VASP_ORDER_STATUS.PENDING]: 'Accepted by VAISP',
  [VASP_ORDER_STATUS.APPROVED]: 'Approved by VAISP',
  [VASP_ORDER_STATUS.AWAITING_ISSUANCE]: 'Awaiting issuance',
  [VASP_ORDER_STATUS.AWAITING_DISTRIBUTION]: 'Awaiting distribution',
  [VASP_ORDER_STATUS.DISTRIBUTING_TOKENS]: 'Distributing tokens',
  [VASP_ORDER_STATUS.TOKENS_DISTRIBUTED]: 'Tokens distributed',
  [VASP_ORDER_STATUS.AWAITING_REGISTRATION]: 'Awaiting DLD registration',
  [VASP_ORDER_STATUS.AWAITING_APPROVAL]: 'Awaiting issuer approval',
  [VASP_ORDER_STATUS.AWAITING_COMPLETE]: 'Awaiting completion',
  [VASP_ORDER_STATUS.COMPLETED]: 'Completed',
  [VASP_ORDER_STATUS.REJECTED]: 'Rejected by VAISP',
  [VASP_ORDER_STATUS.DELETED]: 'Deleted at VAISP',
  [VASP_ORDER_STATUS.CANCELLED]: 'Cancelled at VAISP',
  [VASP_ORDER_STATUS.TOKEN_DISTRIBUTION_REJECTED]: 'Distribution failed',
}

// Shown when no vasp_orders row exists — the order was never sent to the provider.
// Visually distinct from any real vendor status, which is the whole point: it means
// "waiting on us", not "waiting on the vendor".
export const VASP_ORDER_NOT_SENT_LABEL = 'Not sent'

export function vaspOrderStatusColor(status: string | null | undefined): ChipColor {
  if (!status) return 'neutral'
  return VASP_ORDER_STATUS_COLOR[status] ?? 'neutral'
}

export function vaspOrderStatusLabel(status: string | null | undefined): string {
  if (!status) return VASP_ORDER_NOT_SENT_LABEL
  return VASP_ORDER_STATUS_LABEL[status] ?? status
}

// The vendor-side waits that ORDER_PROCESSING_STATUS has no value for. Mirrors
// admin-service's VASP_STATUS_WITHOUT_LOCAL_STAGE: the webhook deliberately
// leaves the local axis alone for these, because inventing a stage for them would
// let an out-of-order redelivery walk an advanced order backwards.
//
// The consequence is that an order parked on one of them looks idle on the local
// pipeline. The lifecycle timeline reads this map onto its active step so the
// order names what it is waiting on instead of just sitting there — which is the
// whole job of the "why is this stuck?" spine.
export const VASP_ORDER_WAIT_DETAIL: Record<string, string> = {
  [VASP_ORDER_STATUS.AWAITING_OPT_IN]:
    "Waiting on the buyer's wallet to opt in to this property's token.",
  [VASP_ORDER_STATUS.PROCESSING_OPT_IN]: 'Ctrl-Alt is authorising the wallet opt-in.',
  [VASP_ORDER_STATUS.AWAITING_EXTERNAL_OPT_IN]:
    "Waiting for the trustline to this property's token to reach the ledger. Open it from wallet management.",
  [VASP_ORDER_STATUS.AWAITING_DISTRIBUTION]: 'Queued behind Ctrl-Alt for token distribution.',
  [VASP_ORDER_STATUS.AWAITING_REGISTRATION]:
    'The trade has not been registered with DLD yet — every order on the offering must be ready first.',
  [VASP_ORDER_STATUS.AWAITING_APPROVAL]:
    'DLD is reviewing the registered trade. It approves or rejects in its own time.',
}

// `processing_opt_in` is the one wait on this map that the trustline's issuer
// authorisation actually resolves, so it gets a per-state refinement rather than
// one sentence covering three different situations (ORD-05).
//
// The base line — "Ctrl-Alt is authorising the wallet opt-in" — is only TRUE for
// `trustlinePeerAuthorized: false`. For `true` Ctrl-Alt has finished and the
// order is waiting on the next step, and for `null` nobody has read the ledger,
// so claiming Ctrl-Alt is mid-authorisation would be a guess dressed as a fact.
//
// Keyed by the resolved three-state rather than the raw flag so a null can never
// fall through to the `false` branch. See @shared/constants/trustlineAuthorization
// for the axis; this map is the order-side copy only.
const PROCESSING_OPT_IN_DETAIL = {
  authorized:
    'Ctrl-Alt has authorised the wallet opt-in. The order is waiting on the next VAISP step, not on the trustline.',
  awaitingIssuer:
    'Ctrl-Alt is authorising the wallet opt-in. Nothing on our side advances it — Ctrl-Alt authorises on its own schedule.',
  notChecked:
    'The order is at the opt-in step and the ledger has not been read, so whether Ctrl-Alt has authorised the trustline is unknown. Check it from wallet management.',
} as const

/**
 * The "why is this stuck?" sentence for a vendor status.
 *
 * `trustlinePeerAuthorized` is optional and three-state; pass it wherever it is
 * known and the `processing_opt_in` line sharpens from "Ctrl-Alt is working on
 * it" to what is actually true. Omitting it keeps the generic wait copy, which
 * is why existing callers (the lifecycle timeline) need no change.
 */
export function vaspOrderWaitDetail(
  status: string | null | undefined,
  trustlinePeerAuthorized?: boolean | null,
): string {
  if (!status) return ''
  if (status === VASP_ORDER_STATUS.PROCESSING_OPT_IN && trustlinePeerAuthorized !== undefined) {
    if (trustlinePeerAuthorized === true) return PROCESSING_OPT_IN_DETAIL.authorized
    if (trustlinePeerAuthorized === false) return PROCESSING_OPT_IN_DETAIL.awaitingIssuer
    return PROCESSING_OPT_IN_DETAIL.notChecked
  }
  return VASP_ORDER_WAIT_DETAIL[status] ?? ''
}

// A broker cancel is only ever accepted while the vendor order is still pre-issuance.
export const VASP_CANCELLABLE_STATUSES: ReadonlySet<string> = new Set([
  VASP_ORDER_STATUS.AWAITING_OPT_IN,
  VASP_ORDER_STATUS.PROCESSING_OPT_IN,
  VASP_ORDER_STATUS.PENDING,
])

// ── Polling ─────────────────────────────────────────────────────────────────

// Real order state now advances on its own via Ctrl-Alt webhooks, so the queue
// must refresh itself. A vendor transition takes minutes, not seconds — 30s for
// the list, 60s for the counts, and only while the page is mounted (the counts
// hook takes its interval as a parameter because the sidebar badge shares the
// same cache entry app-wide).
export const VASP_QUEUE_LIST_POLL_MS = 30_000
export const VASP_QUEUE_COUNTS_POLL_MS = 60_000
